import { useState, useEffect, useCallback } from "react";
import * as XLSX from "xlsx";
import "./CallLogsTable.css";
import { BASE_URL } from "../../pages/Config";
import ButtonLoader from "../Loader/ButtonLoader";


/* ─── API endpoints ──────────────────────────────────────── */
const API_BASE      = `${BASE_URL}api/reporting/call-logs`;
const DOWNLOAD_API  = `${BASE_URL}api/Reporting/download-call-logs`;
const PLATFORMS_API = `${BASE_URL}api/reporting/platforms`;
const STATUSES_API  = `${BASE_URL}api/Reporting/callStatus`;

function emailLocalPart(value) {
    if (value == null || value === "") return "";
    const s = String(value).trim();
    const at = s.indexOf("@");
    return at > 0 ? s.slice(0, at) : s;
}

function findHeaderKey(row, candidates) {
    const keys = Object.keys(row || {});
    const lower = candidates.map((c) => c.toLowerCase());
    return keys.find((k) => lower.includes(String(k).trim().toLowerCase())) || null;
}

/** Enrich downloaded Call Logs sheet: msisdn, DR name, Remarks as text */
function enrichCallLogsWorkbook(arrayBuffer) {
    const workbook = XLSX.read(arrayBuffer, { type: "array", cellDates: true });
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false });

    if (!rows.length) return workbook;

    const sample = rows[0];
    const patientKey = findHeaderKey(sample, ["Patient Email", "patientEmail", "patient_email", "PatientEmail"]);
    const doctorKey = findHeaderKey(sample, ["Doctor Email", "doctorEmail", "doctor_email", "DoctorEmail"]);
    const remarksKey = findHeaderKey(sample, ["Remarks", "remarks", "Remark", "remark"]);

    const enriched = rows.map((row) => {
        const out = { ...row };
        const patientEmail = patientKey ? row[patientKey] : "";
        const doctorEmail = doctorKey ? row[doctorKey] : "";

        // Insert msisdn near Patient Email
        out.msisdn = emailLocalPart(patientEmail);

        // Insert DR name near Doctor Email
        out["DR name"] = emailLocalPart(doctorEmail);

        // Keep Remarks as plain text (e.g. "20219") — all values stay in Remarks column
        if (remarksKey) {
            const rem = row[remarksKey];
            out[remarksKey] = rem == null || rem === "" ? "" : String(rem);
        }

        return out;
    });

    // Preferred column order: original keys + msisdn after patient email + DR name after doctor email
    const originalKeys = Object.keys(sample);
    const ordered = [];
    originalKeys.forEach((key) => {
        ordered.push(key);
        if (patientKey && key === patientKey) ordered.push("msisdn");
        if (doctorKey && key === doctorKey) ordered.push("DR name");
    });
    if (!patientKey && !ordered.includes("msisdn")) ordered.push("msisdn");
    if (!doctorKey && !ordered.includes("DR name")) ordered.push("DR name");

    const newSheet = XLSX.utils.json_to_sheet(enriched, { header: ordered });
    workbook.Sheets[sheetName] = newSheet;
    return workbook;
}

/* ─── Status colour map ──────────────────────────────────── */
const STATUS_MAP = {
    Ringing:                    { dot: "#f59e0b", bg: "#fffbeb", text: "#b45309" },
    Accepted:                   { dot: "#10b981", bg: "#ecfdf5", text: "#065f46" },
    Connected:                  { dot: "#3b82f6", bg: "#eff6ff", text: "#1d4ed8" },
    Ended:                      { dot: "#6b7280", bg: "#f9fafb", text: "#374151" },
    Missed:                     { dot: "#ef4444", bg: "#fef2f2", text: "#b91c1c" },
    "Permission popup appeared":{ dot: "#8b5cf6", bg: "#f5f3ff", text: "#6d28d9" },
};

const TYPE_ICON = { Video: "📹", Audio: "📞", "Incoming Video Call": "📹" };

function fmt(t) {
    if (!t) return "—";
    return new Date(t).toLocaleString("en-PK", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
    });
}

function getPages(cur, total) {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    if (cur <= 4)          return [1, 2, 3, 4, 5, "…", total];
    if (cur >= total - 3)  return [1, "…", total - 4, total - 3, total - 2, total - 1, total];
    return [1, "…", cur - 1, cur, cur + 1, "…", total];
}

function getToday() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function StatusBadge({ s }) {
    const c = STATUS_MAP[s] || { dot: "#6b7280", bg: "#f3f4f6", text: "#374151" };
    return (
        <span className="clt-status-badge" style={{ background: c.bg, color: c.text }}>
            <span
                className={`clt-status-dot${s === "Ringing" ? " ringing" : ""}`}
                style={{ background: c.dot }}
            />
            {s}
        </span>
    );
}

export default function CallLogsTable() {
    /* ── filters ── */
    const [platform, setPlatform]       = useState("");
    const [callStatus, setCallStatus]   = useState("");
    const [fromDate, setFromDate]       = useState(getToday);
    const [toDate, setToDate]           = useState("");
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch]           = useState("");

    /* ── table state ── */
    const [rows, setRows]             = useState([]);
    const [totalRecs, setTotalRecs]   = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [page, setPage]             = useState(1);
    const [pageSize, setPageSize]     = useState(20);
    const [loading, setLoading]       = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [apiError, setApiError]     = useState(false);

    /* ── dropdowns ── */
    const [platforms, setPlatforms] = useState([]);
    const [statuses, setStatuses]   = useState([]);

    useEffect(() => {
        const load = async () => {
            try {
                const [pRes, sRes] = await Promise.all([fetch(PLATFORMS_API), fetch(STATUSES_API)]);
                setPlatforms(pRes.ok ? (await pRes.json())?.data || [] : []);
                setStatuses(sRes.ok  ? (await sRes.json())?.data  || [] : []);
            } catch {
                setPlatforms([]);
                setStatuses([]);
            }
        };
        load();
    }, []);

    const fetchRows = useCallback(async (pg, ps, plt, cs, srch, fd, td) => {
        setLoading(true);
        setApiError(false);
        try {
            const res = await fetch(API_BASE, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    platform: plt, callStatus: cs, search: srch,
                    fromDate: fd || null, toDate: td || null,
                    pageNumber: pg, pageSize: ps,
                }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const j = await res.json();
            setRows(j.data || []);
            setTotalRecs(j.totalRecords || 0);
            setTotalPages(j.totalPages || 1);
        } catch {
            setApiError(true);
            setRows([]);
            setTotalRecs(0);
            setTotalPages(1);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRows(page, pageSize, platform, callStatus, search, fromDate, toDate);
    }, [page, pageSize, platform, callStatus, search, fromDate, toDate, fetchRows]);

    const onFilter = (setter) => (val) => { setter(val); setPage(1); };
    const doSearch = () => { setSearch(searchInput); setPage(1); };
    const clearAll = () => {
        setPlatform(""); setCallStatus(""); setSearch("");
        setSearchInput(""); setFromDate(""); setToDate(""); setPage(1);
    };

    const downloadExcel = async () => {
        setDownloading(true);
        setApiError(false);
        try {
            const res = await fetch(DOWNLOAD_API, {
                method: "POST",
                headers: {
                    accept: "*/*",
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    platform: platform || "",
                    callStatus: callStatus || "",
                    doctorId: "",
                    patientId: "",
                    search: search || "",
                    fromDate: fromDate || "",
                    toDate: toDate || "",
                }),
            });

            if (!res.ok) throw new Error(`HTTP ${res.status}`);

            const contentType = res.headers.get("content-type") || "";
            if (contentType.includes("application/json")) {
                const json = await res.json();
                throw new Error(json.message || "No records found to download.");
            }

            const blob = await res.blob();
            const arrayBuffer = await blob.arrayBuffer();
            const workbook = enrichCallLogsWorkbook(arrayBuffer);
            const out = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
            const outBlob = new Blob([out], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

            const disposition = res.headers.get("content-disposition") || "";
            const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i);
            const filename = match
                ? match[1].replace(/['"]/g, "")
                : `CallLogs_Download_${fromDate || "all"}_to_${toDate || "all"}.xlsx`;

            const url = URL.createObjectURL(outBlob);
            const a = document.createElement("a");
            a.href = url;
            a.setAttribute("download", filename);
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch {
            setApiError(true);
        } finally {
            setDownloading(false);
        }
    };

    const start    = (page - 1) * pageSize + 1;
    const end      = Math.min(page * pageSize, totalRecs);
    const hasActive = platform || callStatus || search;

    return (
        <div className="clt-root">
            {/* ── Section heading ── */}
            <div className="clt-section-header">
                <div>
                    <p className="clt-eyebrow">Reporting</p>
                    <h2 className="clt-title">Call Logs</h2>
                </div>
                <button
                    className="clt-btn-refresh"
                    onClick={() => fetchRows(page, pageSize, platform, callStatus, search, fromDate, toDate)}
                >
                    ↻ Refresh
                </button>
            </div>

            {/* ── Filters card ── */}
            <div className="clt-card">
                <div className="clt-filter-grid">
                    <div className="clt-filter-group">
                        <label className="clt-label">Platform</label>
                        <select
                            className="clt-sel"
                            value={platform}
                            onChange={e => onFilter(setPlatform)(e.target.value)}
                        >
                            <option value="">All Platforms</option>
                            {platforms.map(p => <option key={p}>{p}</option>)}
                        </select>
                    </div>

                    <div className="clt-filter-group">
                        <label className="clt-label">Call Status</label>
                        <select
                            className="clt-sel"
                            value={callStatus}
                            onChange={e => onFilter(setCallStatus)(e.target.value)}
                        >
                            <option value="">All Statuses</option>
                            {statuses.map(s => <option key={s}>{s}</option>)}
                        </select>
                    </div>

                    <div className="clt-filter-group">
                        <label className="clt-label">From</label>
                        <input
                            className="clt-inp"
                            type="date"
                            value={fromDate}
                            onChange={e => onFilter(setFromDate)(e.target.value)}
                        />
                    </div>

                    <div className="clt-filter-group">
                        <label className="clt-label">To</label>
                        <input
                            className="clt-inp"
                            type="date"
                            value={toDate}
                            onChange={e => onFilter(setToDate)(e.target.value)}
                        />
                    </div>

                    <div className="clt-filter-group clt-span-2">
                        <label className="clt-label">Search by phone / email</label>
                        <div className="clt-search-row">
                            <input
                                className="clt-inp"
                                type="text"
                                placeholder="e.g. 03012345678"
                                value={searchInput}
                                onChange={e => setSearchInput(e.target.value)}
                                onKeyDown={e => e.key === "Enter" && doSearch()}
                            />
                            <button className="clt-btn-search" onClick={doSearch}>🔍 Search</button>
                            <button
                                className="clt-btn-export"
                                onClick={downloadExcel}
                                disabled={downloading}
                            >
                                {downloading ? <ButtonLoader label="Downloading…" /> : "⬇ Export Excel"}
                            </button>
                            {hasActive && (
                                <button className="clt-btn-clear" onClick={clearAll}>✕ Clear</button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── API warning ── */}
            {apiError && (
                <div className="clt-warn">⚠️ Unable to load call logs. Please try again.</div>
            )}

            {/* ── Data table card ── */}
            <div className="clt-card">
                {/* toolbar */}
                <div className="clt-toolbar">
                    <span className="clt-rec-txt">
                        {loading
                            ? "Loading…"
                            : totalRecs === 0
                                ? "No records found"
                                : `Showing ${start.toLocaleString()} – ${end.toLocaleString()} of ${totalRecs.toLocaleString()} records`}
                    </span>
                    <div className="clt-toolbar-right">
                        <span className="clt-rows-label">Rows / page</span>
                        <select
                            className="clt-sel clt-sel-sm"
                            value={pageSize}
                            onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                        >
                            {[10, 20, 50, 100].map(n => <option key={n}>{n}</option>)}
                        </select>
                    </div>
                </div>

                {/* table */}
                <div className="clt-table-wrap">
                    <table className="clt-table">
                        <thead>
                            <tr>
                                {["ID","Status","Call Type","Doctor","Patient","Direction","Platform","Date / Time"]
                                    .map(h => <th key={h}>{h}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={8} className="clt-cell-center">
                                        <div className="clt-loader">
                                            <div className="clt-spinner" />
                                            <span className="clt-loader-txt">Fetching records…</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : rows.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="clt-cell-center">
                                        <div className="clt-empty">
                                            <div className="clt-empty-icon">📭</div>
                                            <p>No logs match the selected filters.</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : rows.map(r => (
                                <tr key={r.id} className="clt-row">
                                    <td className="clt-mono clt-id">{r.id}</td>
                                    <td><StatusBadge s={r.callStatus} /></td>
                                    <td>
                                        <span className="clt-type-cell">
                                            <span>{TYPE_ICON[r.callType] || "📞"}</span>
                                            {r.callType}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="clt-doctor-cell">
                                            <span className="clt-avatar">
                                                {(r.doctorEmail || "D")[0].toUpperCase()}
                                            </span>
                                            <span className="clt-mono clt-email">{r.doctorEmail || "—"}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <span className="clt-mono clt-email">{r.patientEmail || "—"}</span>
                                    </td>
                                    <td><span className="clt-tag">{r.logDefinition}</span></td>
                                    <td><span className="clt-ptag">{r.platfrom}</span></td>
                                    <td className="clt-datetime">{fmt(r.time)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* ── Pagination ── */}
                <div className="clt-pg-row">
                    <span className="clt-pg-info">
                        Page <strong>{page}</strong> of <strong>{totalPages}</strong>
                    </span>

                    <div className="clt-pg-btns">
                        <button className="pg-btn" disabled={page === 1} onClick={() => setPage(1)} title="First">«</button>
                        <button className="pg-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)} title="Prev">‹</button>

                        {getPages(page, totalPages).map((p, i) =>
                            p === "…" ? (
                                <span key={"e" + i} className="clt-pg-ellipsis">…</span>
                            ) : (
                                <button
                                    key={p}
                                    className={`pg-btn${p === page ? " pg-active" : ""}`}
                                    onClick={() => setPage(p)}
                                >
                                    {p}
                                </button>
                            )
                        )}

                        <button className="pg-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} title="Next">›</button>
                        <button className="pg-btn" disabled={page === totalPages} onClick={() => setPage(totalPages)} title="Last">»</button>
                    </div>

                    <div className="clt-goto">
                        <span>Go to page</span>
                        <input
                            className="clt-inp clt-goto-inp"
                            type="number"
                            min={1}
                            max={totalPages}
                            onKeyDown={e => {
                                if (e.key === "Enter") {
                                    const v = parseInt(e.target.value);
                                    if (v >= 1 && v <= totalPages) { setPage(v); e.target.value = ""; }
                                }
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}